import { selectionTotals } from "../lib/quote-selection";
/**
 * The page a customer opens.
 *
 * Everything else in this app assumes a signed-in tradesperson on a phone they
 * own, with a data plan the business pays for. This assumes none of that: a
 * stranger, on an unknown device, who has never seen the product, arriving from
 * an email link with one question — what does this cost and do I want it.
 *
 * So it is deliberately not part of the app. No shell, no nav, no sync engine,
 * no theme toggle, nothing to click that is not about this quote. It renders
 * like a document because it is one, and it prints like a document because the
 * first thing a school business manager does with a quote is print it for the
 * person who signs things.
 *
 * The accept button is the entire point of the page, so it is large, it says
 * what will happen, and it cannot be pressed by accident.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiError } from "../lib/api";
import { day, money, qty } from "../lib/format";
import type { PublicQuote as PublicQuoteData } from "../lib/types";

import "../styles/public.css";

export default function PublicQuote() {
  const { token = "" } = useParams();
  const queryClient = useQueryClient();

  const { data, error, isPending } = useQuery({
    queryKey: ["public-quote", token],
    queryFn: () => api.publicQuotes.get(token),
    // A quote does not change while it is being read, and a customer who
    // switches back to this tab should not see it flicker.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: (count, err) => !(err instanceof ApiError && err.status >= 400 && err.status < 500) && count < 2,
  });

  const accept = useMutation({
    mutationFn: (payload: { accepted_by_name: string; signature_url?: string | null; selected_optional_section_ids?: string[]; expected_total_cents?: number }) =>
      api.publicQuotes.accept(token, payload),
    // Patched from the answer rather than refetched. Refetching would be a
    // second round trip on whatever connection they have, and — because every
    // fetch of this page counts as the customer opening it — would leave the
    // office looking at "opened twice" for a customer who opened it once and
    // said yes.
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ["public-quote", token] }); },
  });

  if (isPending) {
    return (
      <Frame>
        <p className="pq-muted">Opening your quote…</p>
      </Frame>
    );
  }

  if (error) return <Unavailable error={error} />;

  return (
    <Frame>
      <Document
        quote={data}
        pdfHref={`/v1/public/quotes/${encodeURIComponent(token)}/pdf`}
        onAccept={accept.mutate}
        accepting={accept.isPending}
        acceptError={accept.error}
        justAccepted={accept.isSuccess}
      />
    </Frame>
  );
}

/* ------------------------------------------------------------------ frame */

function Frame({ children }: { children: React.ReactNode }) {
  return (
    <div className="pq-page">
      <main className="pq-sheet">{children}</main>
      <footer className="pq-foot">
        <span>Sent with Keystone</span>
      </footer>
    </div>
  );
}

/**
 * Every refusal reads the same way to the reader whatever the server said,
 * except the one that is genuinely useful: a quote that lapsed on a date.
 * "Not valid" and "expired" are different problems with different fixes, and
 * making someone ring up to find out which is not service.
 */
function Unavailable({ error }: { error: unknown }) {
  const status = error instanceof ApiError ? error.status : 0;
  const detail = error instanceof ApiError ? error.message : "";

  if (status === 0) {
    return (
      <Frame>
        <div className="pq-empty">
          <h1>No connection</h1>
          <p>We couldn't reach the server. Check your signal and try again.</p>
          <button className="pq-btn pq-btn-quiet" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </Frame>
    );
  }

  return (
    <Frame>
      <div className="pq-empty">
        <h1>{status === 410 ? "This quote has expired" : "This link isn't valid"}</h1>
        <p>{status === 410 ? detail : "It may have been replaced by a newer one, or the address was mistyped."}</p>
        <p className="pq-muted">Get in touch with whoever sent it and they'll send a current link.</p>
      </div>
    </Frame>
  );
}

/* --------------------------------------------------------------- document */

function Document({
  quote,
  pdfHref,
  onAccept,
  accepting,
  acceptError,
  justAccepted,
}: {
  quote: PublicQuoteData;
  pdfHref: string;
  onAccept: (payload: { accepted_by_name: string; signature_url?: string | null; selected_optional_section_ids?: string[]; expected_total_cents?: number }) => void;
  accepting: boolean;
  acceptError: unknown;
  justAccepted: boolean;
}) {
  const decided = quote.status === "accepted";
  const [selected, setSelected] = useState<string[]>(quote.sections.filter(s => s.is_selected).map(s => s.id ?? ""));
  const totals = selectionTotals(quote, selected);
  const price = decided ? { subtotal: quote.subtotal_cents, tax: quote.tax_cents, total: quote.total_cents, deposit: quote.deposit_cents } : totals;
  // Optional sections are quoted but not included in the price. Saying so once,
  // clearly, prevents the phone call where the total is disputed.
  const optional = quote.sections.filter((s) => s.is_optional);

  return (
    <>
      <header className="pq-head">
        <div>
          <p className="pq-from">{quote.issued_by}</p>
          <h1>{quote.title}</h1>
        </div>
        <div className="pq-ref">
          <span className="pq-num">{quote.number}</span>
          {quote.valid_until && !decided && <span className="pq-valid">Valid until {day(quote.valid_until)}</span>}
          {/* A plain link, not a fetch-and-blob: the browser's own download
              handling knows what to do with a PDF on every platform this will
              be opened on, and an iOS Safari that refuses a generated blob is
              not a bug anybody wants to debug from a customer's phone. */}
          <a className="pq-download" href={pdfHref} target="_blank" rel="noreferrer">
            Download PDF
          </a>
        </div>
      </header>

      {decided && (
        <div className="pq-accepted" role="status">
          <strong>Accepted{quote.accepted_by_name ? ` by ${quote.accepted_by_name}` : ""}</strong>
          <span>
            {quote.accepted_at ? `on ${day(quote.accepted_at)}` : ""} — we'll be in touch to book it in.
          </span>
        </div>
      )}

      <dl className="pq-meta">
        <div>
          <dt>Prepared for</dt>
          <dd>{quote.customer_name}</dd>
        </div>
        <div>
          <dt>Site</dt>
          <dd>
            {quote.site_name}
            {quote.site_address && <span className="pq-addr">{quote.site_address}</span>}
          </dd>
        </div>
      </dl>

      {quote.sections.map((section, index) => (
        <section className="pq-section" key={`${section.title}-${index}`}>
          <h2>
            {section.title}
            {section.is_optional && <span className="pq-opt">{decided && section.is_selected ? "Optional — included" : "Optional"}</span>}
          </h2>
          {section.is_optional && !decided && <label><input type="checkbox" checked={selected.includes(section.id ?? "")} disabled={accepting} onChange={e => setSelected(e.target.checked ? [...selected, section.id ?? ""] : selected.filter(id => id !== section.id))} /> Include this option</label>}
          <table className="pq-lines">
            <thead>
              <tr>
                <th scope="col">Item</th>
                <th scope="col" className="pq-r">
                  Qty
                </th>
                <th scope="col" className="pq-r">
                  Each
                </th>
                <th scope="col" className="pq-r">
                  Amount
                </th>
              </tr>
            </thead>
            <tbody>
              {section.lines.map((line, i) => (
                <tr key={`${line.description}-${i}`}>
                  <td>{line.part_number && <div className="tiny muted">{line.part_number}</div>}{line.description}</td>
                  <td className="pq-r pq-num-cell">
                    {qty(line.qty)}
                    {line.uom !== "ea" && <span className="pq-uom"> {line.uom}</span>}
                  </td>
                  <td className="pq-r pq-num-cell">
                    {line.unit_sell_cents === 0 ? "—" : money(line.unit_sell_cents)}
                  </td>
                  {/* A zero-priced line is something thrown in, and "$0.00"
                      reads as a mistake or a haggling opportunity. Say what it
                      actually is. */}
                  <td className="pq-r pq-num-cell">
                    {line.amount_cents === 0 ? <span className="pq-included">Included</span> : money(line.amount_cents)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              {/* Four real cells rather than a colSpan. A spanning cell fights
                  `table-layout: fixed` — it claims more than the columns it
                  covers, and on a phone, where the unit-price column is
                  hidden, that pushed the section total off the side of the
                  page. Empty cells cost nothing and line up exactly. */}
              <tr>
                <th scope="row">{section.is_optional ? "If included" : "Section total"}</th>
                <td />
                <td />
                <td className="pq-r pq-num-cell">{money(section.amount_cents)}</td>
              </tr>
            </tfoot>
          </table>
        </section>
      ))}

      <section className="pq-totals" aria-label="Totals">
        <div>
          <span>Subtotal</span>
          <span className="pq-num-cell">{money(price.subtotal)}</span>
        </div>
        <div>
          <span>GST</span>
          <span className="pq-num-cell">{money(price.tax)}</span>
        </div>
        <div className="pq-grand">
          <span>Total</span>
          <span className="pq-num-cell">{money(price.total)}</span>
        </div>
        {price.deposit > 0 && (
          <div className="pq-deposit">
            <span>Deposit on acceptance</span>
            <span className="pq-num-cell">{money(price.deposit)}</span>
          </div>
        )}
      </section>

      {optional.length > 0 && (
        <p className="pq-note">
          Only selected options are included in the total. The PDF records saved selections; your choices are saved when you accept.
        </p>
      )}

      {(quote.discount_bp ?? 0) > 0 && <p className="pq-note">Prices include a {(quote.discount_bp ?? 0) / 100}% discount.</p>}
      {[["Scope and inclusions", quote.scope], ["Exclusions", quote.exclusions]].map(([label, content]) => content && <section className="pq-terms" key={label}><h2>{label}</h2><p style={{ whiteSpace: "pre-wrap" }}>{content}</p></section>)}
      {quote.terms && (
        <section className="pq-terms">
          <h2>Terms</h2>
          {quote.terms
            .split("\n")
            .filter((line) => line.trim())
            .map((line, i) => (
              <p key={i}>{line}</p>
            ))}
        </section>
      )}

      {!decided && (
        <AcceptPanel
          total={price.total}
          deposit={price.deposit}
          onAccept={payload => onAccept({ ...payload, selected_optional_section_ids: selected, expected_total_cents: price.total })}
          busy={accepting}
          error={acceptError}
        />
      )}

      {justAccepted && (
        <p className="pq-thanks" role="status">
          Thank you — that's booked with us.
        </p>
      )}
    </>
  );
}

/* ----------------------------------------------------------------- accept */

function AcceptPanel({
  total,
  deposit,
  onAccept,
  busy,
  error,
}: {
  total: number;
  deposit: number;
  onAccept: (payload: { accepted_by_name: string; signature_url?: string | null; selected_optional_section_ids?: string[]; expected_total_cents?: number }) => void;
  busy: boolean;
  error: unknown;
}) {
  const [name, setName] = useState("");
  const [signature, setSignature] = useState<string | null>(null);
  const tooShort = name.trim().length < 2;

  return (
    <section className="pq-accept" aria-label="Accept this quote">
      <h2>Accept this quote</h2>
      <p>
        Accepting is your authority for us to proceed at <strong>{money(total)}</strong>
        {deposit > 0 ? (
          <>
            , with a deposit of <strong>{money(deposit)}</strong> invoiced now
          </>
        ) : null}
        . We'll contact you to arrange a date.
      </p>

      <label className="pq-field">
        <span>Your name</span>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. J. Whitcombe"
          autoComplete="name"
          maxLength={160}
          disabled={busy}
        />
      </label>

      <SignaturePad value={signature} onChange={setSignature} disabled={busy} />

      {error instanceof Error && <p className="pq-error">{error.message}</p>}

      <button
        className="pq-btn"
        disabled={tooShort || busy}
        onClick={() => onAccept({ accepted_by_name: name.trim(), signature_url: signature })}
      >
        {busy ? "Sending…" : `Accept ${money(total)}`}
      </button>
      <p className="pq-fine">
        Your name and the time you accepted are recorded against this quote. Signing is optional.
      </p>
    </section>
  );
}

/**
 * A finger on glass, or a mouse. Kept deliberately small: the legal weight here
 * is the token plus the typed name plus the timestamp — the drawing is what
 * makes a person feel they have signed something, which is worth having but is
 * not what anybody would rely on in a dispute.
 */
function SignaturePad({
  value,
  onChange,
  disabled,
}: {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
  disabled: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const drew = useRef(false);

  const context = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    return canvas.getContext("2d");
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    // Back the canvas at device resolution or the stroke looks like a fax.
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return;
    canvas.width = Math.round(rect.width * ratio);
    canvas.height = Math.round(rect.height * ratio);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#101a1c";
  }, []);

  const at = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const start = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    const ctx = context();
    if (!ctx) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drawing.current = true;
    const { x, y } = at(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return;
    const ctx = context();
    if (!ctx) return;
    const { x, y } = at(event);
    ctx.lineTo(x, y);
    ctx.stroke();
    drew.current = true;
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    const canvas = canvasRef.current;
    if (canvas && drew.current) onChange(canvas.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = context();
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    drew.current = false;
    onChange(null);
  };

  return (
    <div className="pq-sign">
      <div className="pq-sign-head">
        <span>Signature (optional)</span>
        {value && (
          <button type="button" className="pq-link" onClick={clear} disabled={disabled}>
            Clear
          </button>
        )}
      </div>
      <canvas
        ref={canvasRef}
        className="pq-canvas"
        aria-label="Sign here with your finger or mouse"
        onPointerDown={start}
        onPointerMove={move}
        onPointerUp={end}
        onPointerLeave={end}
        onPointerCancel={end}
      />
    </div>
  );
}
